// --------------------------------------------------------------
// Created On: 2022-07-28
// Author: Zachary Thomas
//
// Last Modified: 2026-04-14
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";
import Error from "../../../components/Error/Error";
import Spinner from "../../../components/Spinner/Spinner";
import apiRequest from "../../../utilities/api/apiRequest";
import useReduxSelector from "../../../hooks/useReduxSelector";
import userHasPermission from "../../../utilities/userHasPermission";
import { API } from "../../../constants/miscellaneous";
import useApi from "../../../hooks/useApi";
import { TRANSFER_ASSET_TYPES } from "../../../constants/reducerActions";
import AssetDetails from "../../../components/AssetDetails/AssetDetails";
import { ACCEPT_ASSET_TRANSFERS_PERMISSION, DECLINE_ASSET_TRANSFERS_PERMISSION } from "../../../constants/permissions";
import getApiError from "../../../utilities/api/getApiError";
import styles from "./IncomingTransferAssetModal.module.scss";

// Modal for accepting or declining an asset transfer directed to your current company.
export default function IncomingTransferAssetModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [asset, setAsset] = useState<DetailedAsset | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const userCompanyId = currentUser.isInSubsidiary ? currentUser.subsidiaryCompanyId : currentUser.companyId;

  // Get the current pending asset information.
  useApi(
    () => {
      if (props.asset !== undefined && props.asset.assetId > 0) {
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${userCompanyId}/pending/asset/${props.asset.assetId}`,
    },
    async (response: Response, responseBody: AssetResponseBody) => {
      if (response.ok && responseBody) {
        const assetDetails = {
          assetId: responseBody.assetId,
          name: responseBody.name,
          nickname: responseBody.nickname,
          productModel: responseBody.productModel,
          productIdentifier: responseBody.productIdentifier,
          productManufacturer: responseBody.productManufacturer,
          deviceType: responseBody.deviceType,
          deviceTypeName: responseBody.deviceTypeName,
          deviceIdentifier: responseBody.deviceIdentifier,
          controllerModel: responseBody.controllerModel,
          controllerName: responseBody.controllerName,
          alertThresholdPermission: responseBody.alertThresholdPermission,
          operateAccessPermission: responseBody.operateAccessPermission,
        };
        setAsset(assetDetails);
      } else {
        setAsset(null);
        setErrorMessage("Internal server error. Unable to get asset information.");
      }
      setLoading(false);
    },
    [JSON.stringify(props.asset)]
  );

  // Accept or rejects the current asset.
  async function respondToRequest(isAccepted: boolean): Promise<void> {
    setShowConfirmDelete(false);
    const requestBody = {
      isAccepted: isAccepted,
    };

    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${userCompanyId}/transfer/${props.asset.assetId}/response`,
      "POST",
      requestBody
    )) as [Response, ResponseBody];
    setLoading(false);

    if (response.ok) {
      props.onAction({
        type: TRANSFER_ASSET_TYPES.DELETE_ASSET,
        payload: {
          assetId: props.asset.assetId,
        },
      });
    } else {
      setErrorMessage(await getApiError(response, "Unable to respond to pending transfer request."));
    }
  }

  return (
    <div>
      <Spinner loading={loading} />

      <Modal
        show={true}
        onHide={() => props.onClose()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="lg"
        centered
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">Review Asset Transfer Request</h5>
        </ModalHeader>

        <ModalBody>
          <div className="text-center">
            <span className="mt-3">Asset &apos;</span>
            <b>{props.asset.name}</b>
            <span>&nbsp;is being sent from company &apos;</span>
            <b>{props.asset.companyName}</b>
            <span>&apos;.</span>
            <br />
            {asset !== null && (
              <AssetDetails
                name={asset.name}
                nickname={asset.nickname}
                productModel={asset.productModel}
                productIdentifier={asset.productIdentifier}
                productManufacturer={asset.productManufacturer}
                deviceType={asset.deviceType}
                deviceTypeName={asset.deviceTypeName}
                deviceIdentifier={asset.deviceIdentifier}
                controllerModel={asset.controllerName}
                loading={false}
                isRented={false}
              />
            )}

            <div className="my-2">
              <Error message={errorMessage} />
            </div>
          </div>
        </ModalBody>

        <ModalFooter className={styles.footer}>
          <Fragment>
            {userHasPermission([[DECLINE_ASSET_TRANSFERS_PERMISSION]]) && (
              <button
                data-test="incoming-asset-decline-asset-button"
                className={`${styles.btn} btn btn-danger me-auto" type="button`}
                onClick={() => setShowConfirmDelete(true)}
              >
                Decline Asset
              </button>
            )}
          </Fragment>

          {userHasPermission([[ACCEPT_ASSET_TRANSFERS_PERMISSION]]) && (
            <button
              data-test="incoming-asset-accept-asset-button"
              className={`${styles.btn} btn btn-success me-auto" type="button`}
              onClick={() => respondToRequest(true)}
            >
              Accept Asset
            </button>
          )}

          <button className={`${styles.btn} btn btn-secondary`} type="button" onClick={() => props.onClose()}>
            Close
          </button>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        showModal={showConfirmDelete}
        title={`Decline '${props.asset.name}'`}
        content={`Are you sure that you want to decline the asset '${props.asset.name}'?`}
        yesText="Decline Asset"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmDelete(false)}
        onYes={() => respondToRequest(false)}
        onNo={() => setShowConfirmDelete(false)}
      />
    </div>
  );
}

interface Props {
  asset: TransferAsset;
  onClose: () => void;
  onAction: (action: RentalAction) => void;
}

interface AssetResponseBody {
  assetId: number;
  name: string;
  nickname: string;
  productModel: string;
  productIdentifier: string;
  productManufacturer: string;
  deviceType: string;
  deviceTypeName: string;
  deviceIdentifier: string;
  controllerModel: string;
  controllerName: string;
  alertThresholdPermission: boolean;
  operateAccessPermission: boolean;
  error?: string;
}

interface ResponseBody {
  error?: string;
}

interface TransferAsset {
  assetId: number;
  name: string;
  companyName: string;
}

interface RentalAction {
  type: string;
  payload: RentalPayload;
}

interface RentalPayload {
  assets?: TransferAsset[];
  asset?: TransferAsset;
  assetId?: number;
  companyId?: number;
}

interface DetailedAsset {
  assetId: number;
  name: string;
  nickname: string;
  productModel: string;
  productIdentifier: string;
  productManufacturer: string;
  deviceType: string;
  deviceTypeName: string;
  deviceIdentifier: string;
  controllerModel: string;
  controllerName: string;
  alertThresholdPermission: boolean;
  operateAccessPermission: boolean;
}
